import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Dashboard from '../components/Dashboard';
import { useDashboardData } from '../hooks/useDashboardData';
import { getCookie } from '../services/CookieService';
import { DashboardProvider } from '../hooks/useDashboardContext';
import { EmergencyProvider } from '../contexts/EmergencyContext';
import { GlobalVariablesProvider } from '../contexts/GlobalVariablesContext';
import { DashboardPopupProvider } from '../contexts/DashboardPopupContext';
import './PrintDashboardPage.css';


const PrintDashboardContent = ({ dashboardId, fitParam, tenantFromCookie }) => {
  const navigate = useNavigate();
  const { dashboardTemplate, templateLoading } = useDashboardData(dashboardId, true);
  const pagePrintedRef = useRef(false);
  const [nowString, setNowString] = useState(() => new Date().toLocaleString());
  const [targetContainerWidth, setTargetContainerWidth] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('enerwiser.print.snapshot');
      if (raw) {
        const snap = JSON.parse(raw);
        if (snap && typeof snap.containerWidthPx === 'number' && snap.containerWidthPx > 0) {
          setTargetContainerWidth(snap.containerWidthPx);
        }
      }
    } catch (_) {}

    const t = setInterval(() => setNowString(new Date().toLocaleString()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (pagePrintedRef.current) return;
    let holdTimer = null;
    let rafId1 = null;
    let rafId2 = null;
    const handleReady = () => {
      if (pagePrintedRef.current) return;
      // 두 프레임(주요 레이아웃 안정화) + 800ms 추가 대기 후 인쇄
      const startHold = () => {
        holdTimer = setTimeout(() => {
          if (pagePrintedRef.current) return;
          try {
            window.print();
            pagePrintedRef.current = true;
          } catch (_) {}
        }, 2000);
      };
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          startHold();
        });
      });
    };
    window.addEventListener('dashboard-ready-for-print', handleReady);
    // 폴백: 3초 내 이벤트가 안 오면 강제 인쇄
    const fallbackTimer = setTimeout(() => {
      if (!pagePrintedRef.current) handleReady();
    }, 3000);
    return () => {
      window.removeEventListener('dashboard-ready-for-print', handleReady);
      clearTimeout(fallbackTimer);
      if (holdTimer) clearTimeout(holdTimer);
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
    };
  }, [templateLoading]);

  useEffect(() => {
    const handleAfterPrint = () => {
      try {
        navigate(-1);
      } catch (_) {
        navigate('/');
      }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [navigate]);

  const title = dashboardTemplate?.title || '대시보드';
  const tenantDisplay = tenantFromCookie === 'ess' ? 'ESS' : tenantFromCookie === 'evcheck' ? 'EVCheck' : (tenantFromCookie || '');

  const [scale, setScale] = useState(0.54);

  useEffect(() => {
    if (fitParam) {
      const n = parseFloat(fitParam);
      if (!isNaN(n) && n > 0 && n <= 1) {
        setScale(n);
        return;
      }
    }

    const calculateScale = () => {
      const dashboardWidth = targetContainerWidth || 1200;
      const windowWidth = window.innerWidth || 800;
      const printableWidth = windowWidth * 0.54;
      const newScale = Math.min(1, Math.max(0.2, printableWidth / dashboardWidth));
      setScale(newScale);
    };

    const timer = setTimeout(calculateScale, 300);
    window.addEventListener('resize', calculateScale);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculateScale);
    };
  }, [fitParam, targetContainerWidth]);

  return (
    <div className="print-page">
      <div className="print-frame">
        <div className="print-header">
          <div className="print-header-title">{title}</div>
          <div className="print-header-meta">
            {tenantDisplay && <span className="meta-item">테넌트: {tenantDisplay}</span>}
            <span className="meta-item">날짜: {nowString}</span>
          </div>
        </div>
        <div
          ref={contentRef}
          className="print-content"
          style={{
            transform: `scale(${fitParam ? parseFloat(fitParam) : scale})`,
            transformOrigin: 'top left',
            width: targetContainerWidth ? `${targetContainerWidth}px` : undefined
          }}
        >
          <Dashboard dashboardId={dashboardId} printMode={true} />
        </div>
      </div>
    </div>
  );
};

const PrintDashboardPage = () => {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idFromQuery = params.get('id');
  const fitParam = params.get('fit');

  const dashboardIdFromCookie = getCookie('dashboard_id');
  const dashboardId = idFromQuery || dashboardIdFromCookie || null;
  const tenantFromCookie = getCookie('current_tenant');

  return (
    <DashboardProvider>
      <EmergencyProvider>
        <GlobalVariablesProvider>
          <DashboardPopupProvider>
            <PrintDashboardContent
              dashboardId={dashboardId}
              fitParam={fitParam}
              tenantFromCookie={tenantFromCookie}
            />
          </DashboardPopupProvider>
        </GlobalVariablesProvider>
      </EmergencyProvider>
    </DashboardProvider>
  );
};

export default PrintDashboardPage;
